import React from 'react';
import { Cell, ColumnInterface } from 'react-table';
import cx from 'classnames';

import { IconMusicFile, Tooltip } from '@trqk/components';

import { subRowHeight } from '../../constants';

interface TrackNameCellProps {
    cell: Cell<TrqkGapChannelAudio, GapReportAudioTrackNameCellValue>;
    column: ColumnInterface<TrqkGapChannelAudio>;
}

const labelClasses = 'respond-to-mouse leading-tight break-words px-1 text-xs';
const iconWidth = 24;

/**
 * Displays identifying details about a row in the ViewGapAnalysisTable. If the
 * `detectionTitle` and `audioFilename` are available, they are each shown on a separate line. If
 * only `audioFilename` is available, it is shown alone.
 * @param props - {@link TrackNameCellProps}
 * @returns JSX
 */
export function TrackNameCell({
    cell,
    column,
}: TrackNameCellProps): JSX.Element {
    const width = (column?.width as number) ?? 100;
    const finalWidth = width - iconWidth - 10;
    const audioFilename = cell?.value?.audioFilename ?? null;
    const detectionTitle = cell?.value?.detectionTitle ?? null;

    if (detectionTitle === null && audioFilename) {
        return (
            <Tooltip
                message={audioFilename}
                position="right"
                minWidth={250}
                maxWidth={250}
            >
                <div
                    className="flex items-center"
                    style={{ height: subRowHeight }}
                >
                    <IconMusicFile
                        addClasses="mx-1"
                        fillColor="secondary-500"
                    />
                    <p
                        className="respond-to-mouse leading-tight clamp-2-lines-break-words text-xs"
                        style={{
                            width: finalWidth,
                        }}
                    >
                        {audioFilename}
                    </p>
                </div>
            </Tooltip>
        );
    }

    return (
        <Tooltip
            message={
                <div>
                    {detectionTitle && (
                        <p className="font-medium">{detectionTitle}</p>
                    )}
                    {audioFilename && <p>{audioFilename}</p>}
                </div>
            }
            position="right"
            minWidth={250}
            maxWidth={250}
        >
            <div className="flex items-center" style={{ height: subRowHeight }}>
                <IconMusicFile
                    addClasses="mx-1"
                    fillColor="secondary-500"
                    width={iconWidth}
                />
                <div>
                    {detectionTitle && (
                        <p
                            className={cx(
                                labelClasses,
                                'font-medium text-black',
                                {
                                    'clamp-2-lines-break-words':
                                        audioFilename === null,
                                    'clamp-1-line': audioFilename !== null,
                                },
                            )}
                            style={{
                                width: finalWidth,
                            }}
                        >
                            {detectionTitle}
                        </p>
                    )}
                    {audioFilename && (
                        <p
                            className={cx(labelClasses, {
                                'clamp-2-lines-break-words':
                                    detectionTitle === null,
                                'clamp-1-line': detectionTitle !== null,
                            })}
                            style={{
                                width: finalWidth,
                            }}
                        >
                            {audioFilename}
                        </p>
                    )}
                </div>
            </div>
        </Tooltip>
    );
}
